import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FormPromo from './../FormSection/FormPromo';
import ContactLayoutSkeleton from './ContactLayoutSkeleton';
import ErrorState from './../../ErrorState/ErrorState';
import { useCachedFetch } from '../../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../../AnimatedOnScroll';
import SEO from './../../SEO/SEO';
import { usePageSEO } from '../../../hooks/usePageSEO';
import './ContactPromoLayout.scss';

const ContactPromoLayout = ({ promoCode, hideHeaderFooterSetter, hideLanguageSwitcherSetter }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    const seo = usePageSEO('contactPromo');

    const { data: contactData, loading: dataLoading, error: dataError, refetch } = useCachedFetch('/data/locales/contact.json');
    const { data: discountData, loading: discountLoading } = useCachedFetch('/data/locales/discount.json');

    const [offerDiscount, setOfferDiscount] = useState('');
    const [offerServiceName, setOfferServiceName] = useState('');

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (hideLanguageSwitcherSetter) {
            hideLanguageSwitcherSetter(true);
            return () => hideLanguageSwitcherSetter(false);
        }
    }, [hideLanguageSwitcherSetter]);

    useEffect(() => {
        if (discountLoading || !discountData?.DiscountData?.offers || !promoCode) return;

        const foundOffer = discountData.DiscountData.offers.find(offer => {
            return Object.values(offer.translations).some(translation =>
                translation.promocode?.toUpperCase() === promoCode.toUpperCase()
            );
        });

        if (foundOffer) {
            const discountValue = foundOffer.discount.replace('-', '').replace('%', '');
            setOfferDiscount(discountValue);

            const currentTranslation = foundOffer.translations[currentLang] || foundOffer.translations.en;
            const serviceTitle = currentTranslation.title.join(' ');
            setOfferServiceName(serviceTitle);
        }
    }, [discountData, promoCode, currentLang, discountLoading]);

    useEffect(() => {
        if (dataLoading) return;

        if (dataError) {
            setHasError(true);
            setIsLoading(false);
            return;
        }

        const pageData = contactData?.ContactData?.PromoPage?.[currentLang] || contactData?.ContactData?.PromoPage?.en;

        if (pageData &&
            pageData.mainTitle &&
            pageData.mainSubtitle &&
            pageData.contentAccent &&
            pageData.contentTitle &&
            pageData.contentSubtitle) {
            setHasError(false);
        } else {
            setHasError(true);
        }

        setIsLoading(false);
    }, [dataLoading, dataError, contactData, currentLang]);

    const promoData = useMemo(() => {
        return contactData?.ContactData?.PromoPage?.[currentLang] || contactData?.ContactData?.PromoPage?.en || {};
    }, [contactData, currentLang]);

    const { mainTitle, mainSubtitle, contentAccent, contentTitle, contentSubtitle } = promoData;

    if (isLoading) {
        return <ContactLayoutSkeleton type="promo" />;
    }

    if (hasError || !promoData) {
        return <ErrorState onRetry={handleRetry} />;
    }

    return (
        <>
            <SEO
                title={seo.title}
                description={seo.description}
                keywords={seo.keywords}
                type={seo.type}
                noIndex={seo.noIndex}
                noFollow={seo.noFollow}
            />
            <div className="promo__contact__wrapper">
                <div className="promo__contact__wrapper__content">
                    <div className="promo__contact__wrapper__content__info">
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <p className="promo__content__info__accent">{contentAccent}</p>
                        </AnimatedOnScroll>
                        <AnimatedOnScroll animation="fade-up" delay={100}>
                            <h4 className="promo__content__info__title">{contentTitle}</h4>
                        </AnimatedOnScroll>
                        <AnimatedOnScroll animation="fade-up" delay={200}>
                            <h5 className="promo__content__info__subtitle">{contentSubtitle}</h5>
                        </AnimatedOnScroll>
                    </div>
                </div>
                <div className="promo__contact__wrapper__form">
                    <AnimatedOnScroll animation="fade-down" delay={0}>
                        <div className='promo__contact__wrapper__form__info'>
                            <h5 className='promo__form__info__title'>{mainTitle}</h5>
                            <p className="promo__form__info__subtitle">{mainSubtitle}</p>
                        </div>
                    </AnimatedOnScroll>
                    <FormPromo
                        promoCode={promoCode}
                        discount={offerDiscount}
                        serviceName={offerServiceName}
                        hideHeaderFooterSetter={hideHeaderFooterSetter}
                    />
                </div>
            </div>
        </>
    );
};

export default React.memo(ContactPromoLayout);